import { Field as FieldType } from "@/models/new/forms.types"
import { FormResponse, ResponseValue } from "@/models/new/formResponse.types"
import { SelectOption } from "@/types/select-options"
import { ScopedOrganizationEntity } from "@/models/scoped-organization-hierarchy"
import type { ConditionValueAliases } from "@/utils/conditionalDisplay"

import DatePicker from "./fields/DatePicker"
import BooleanField from "./fields/Boolean"
import TextField from "./fields/String"
import TextAreaField from "./fields/TextArea"
import SelectField from "./fields/Select"
import GeolocationField from "./fields/Geolocation"
import OrganizationField from "./fields/Organization"
import SignatureField from "./fields/Signature"
import FileField from "./fields/File"
import DynamicGroupField from "./fields/DynamicGroup"
import type { PendingReportUpload, ReportCreationMedia } from "./report-creation-media"

interface Props {
  field: FieldType
  value?: ResponseValue
  onChange: (fieldId: string, value: ResponseValue) => void
  onDynamicGroupInstanceRemove?: (
    field: Extract<FieldType, { type: "dynamicGroup" }>,
    instance: number,
  ) => void
  onDynamicGroupInstancesChange?: (groupId: string, instances: number[] | null) => void
  listOptions?: Record<string, SelectOption[]>
  conditionValueAliases?: ConditionValueAliases
  organizations?: ScopedOrganizationEntity[]
  formResponse?: FormResponse
  fieldMediaLocators?: Set<string>
  selectedFieldMediaLocator?: string | null
  canCreateFieldMedia?: boolean
  onNavigateToFieldMedia?: (locator: string, options?: { openComposer?: boolean }) => void
  isCreating?: boolean
  pendingUploads?: PendingReportUpload[]
  onPendingUploadsChange?: (uploads: PendingReportUpload[]) => void
  creationMedia?: ReportCreationMedia
  onCreationMediaChange?: (media: ReportCreationMedia) => void
}

const Field = (props: Props) => {
  const { field, value, onChange } = props

  switch (field.type) {
    case "dynamicGroup":
      return (
        <DynamicGroupField
          field={field}
          formResponse={props.formResponse!}
          onChange={onChange}
          onRemoveInstance={props.onDynamicGroupInstanceRemove}
          onInstancesChange={
            props.onDynamicGroupInstancesChange
              ? (instances) => props.onDynamicGroupInstancesChange!(field.id, instances)
              : undefined
          }
          listOptions={props.listOptions}
          conditionValueAliases={props.conditionValueAliases}
          organizations={props.organizations}
          fieldMediaLocators={props.fieldMediaLocators}
          selectedFieldMediaLocator={props.selectedFieldMediaLocator}
          canCreateFieldMedia={props.canCreateFieldMedia}
          onNavigateToFieldMedia={props.onNavigateToFieldMedia}
          isCreating={props.isCreating}
          creationMedia={props.creationMedia}
          onCreationMediaChange={props.onCreationMediaChange}
        />
      )

    case "datepicker":
      return <DatePicker field={field} value={value} onChange={onChange} />

    case "paragraph":
      return <TextAreaField field={field} value={value} onChange={onChange} />

    case "text":
      return <TextField field={field} value={value} onChange={onChange} />

    case "select":
      return (
        <SelectField
          field={field}
          value={value}
          onChange={onChange}
          listOptions={props.listOptions}
        />
      )

    case "geolocation":
      return (
        <GeolocationField
          field={field}
          value={value}
          onChange={onChange}
          isCreating={props.isCreating}
        />
      )

    case "boolean":
      return <BooleanField field={field} value={value} onChange={onChange} />

    case "organization":
      return (
        <OrganizationField
          field={field}
          value={value}
          onChange={onChange}
          organizations={props.organizations}
          isRequired={field.required}
        />
      )

    case "signature":
      return (
        <SignatureField
          field={field}
          value={value}
          onChange={onChange}
          isCreating={props.isCreating}
        />
      )

    case "file":
      return (
        <FileField
          field={field}
          value={value}
          onChange={onChange}
          isCreating={props.isCreating}
          pendingUploads={props.pendingUploads}
          onPendingUploadsChange={props.onPendingUploadsChange}
        />
      )

    default:
      console.warn(`Unhandled field type: ${(field as FieldType).type}`)
      return (
        <TextField
          field={field as Extract<FieldType, { type: "text" }>}
          value={value}
          onChange={onChange}
        />
      )
  }
}

export default Field
